import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Chip,
  Button,
  Divider,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import { 
  Info, 
  PlayArrow, 
  Lock, 
  CheckCircle,
  AccessTime,
  VideoLibrary,
  ArrowForward,
  Assignment,
  EmojiEvents
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Course, Lesson, Purchase, Unlocks, LessonProgress, Exam } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import PurchaseModal from '../components/Course/PurchaseModal';
import PaymentHelpModal from '../components/Course/PaymentHelpModal';
import Breadcrumb from '../components/Common/Breadcrumb';

const CourseDetail: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [userPurchases, setUserPurchases] = useState<Purchase[]>([]);
  const [userUnlocks, setUserUnlocks] = useState<Unlocks[]>([]);
  const [lessonProgress, setLessonProgress] = useState<LessonProgress[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [examAttempts, setExamAttempts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [paymentHelpModalOpen, setPaymentHelpModalOpen] = useState(false);

  const fetchCourseData = useCallback(async (showPageLoader: boolean = true) => {
    if (!id) return;

    try {
      if (showPageLoader) {
        setLoading(true);
      }
      const { results: allCourses } = await apiService.getPublishedCourses(1, 500);
      const courseData = allCourses.find((c) => c.hashed === id);

      if (!courseData) {
        throw new Error('Course not found');
      }
      const lessonsData = await apiService.getLessonsByCourse(courseData.id);
      let purchases: Purchase[] = [];
      let unlocks: Unlocks[] = [];
      let progressList: LessonProgress[] = [];
      if (isAuthenticated) {
        try {
          purchases = await apiService.getMyPurchases();
        } catch (error) {
          console.log('Could not fetch purchases:', error);
        }
        try {
          unlocks = await apiService.getMyUnlocks();
        } catch (error) {
          console.log('Could not fetch unlocks:', error);
        }
        try {
          progressList = await apiService.getMyProgress();
        } catch (error) {
          console.log('Could not fetch progress:', error);
        }
        try {
          const examsData = await apiService.getExamsByCourse(courseData.id);
          setExams(examsData);
        } catch (error) {
          console.log('Could not fetch exams:', error);
        }
        try {
          const allAttempts = await apiService.getMyExamAttempts();
          setExamAttempts(allAttempts || []);
        } catch (error) {
          console.log('Could not fetch exam attempts:', error);
        }
      }

      setCourse(courseData);
      setLessons(lessonsData);
      setUserPurchases(purchases);
      setUserUnlocks(unlocks);
      setLessonProgress(progressList);
    } catch (error) {
      console.error('Error fetching course data:', error);
      navigate('/courses');
    } finally {
      if (showPageLoader) {
        setLoading(false);
      }
    }
  }, [id, navigate, isAuthenticated]);

  useEffect(() => {
    fetchCourseData(true);
  }, [fetchCourseData]);

  // Helper function to check if user has access to this course (unlocks)
  const hasPurchasedCourse = (): boolean => {
    if (!course || !isAuthenticated) return false;
    
    // 1) Prefer purchases as source of truth for expiry (matches backend check_user_course_access)
    const coursePurchases = userPurchases.filter(
      purchase => purchase.course_id === course.id && purchase.is_verified
    );
    if (coursePurchases.length > 0) {
      const hasActivePurchase = coursePurchases.some(
        purchase => purchase.is_active && !purchase.is_expired
      );
      // If there is any purchase record for this course, we NEVER fall back to unlocks.
      // Expiry is fully controlled by the purchase.
      return hasActivePurchase;
    }

    // 2) If there is no active purchase, fall back to unlocks (e.g. manual unlocks, legacy data)
    const unlock = userUnlocks.find(unlock => unlock.course_id === course.id);
    if (unlock) {
      return unlock.is_active && !unlock.is_expired;
    }

    return false;
  };

  // Helper: has the user ever purchased/unlocked this course (even if now expired)?
  const hasEverPurchasedCourse = (): boolean => {
    if (!course || !isAuthenticated) return false;
    const hadUnlock = userUnlocks.some(unlock => unlock.course_id === course.id);
    const hadPurchase = userPurchases.some(purchase => 
      purchase.course_id === course.id && purchase.is_verified
    );
    return hadUnlock || hadPurchase;
  };

  // Helper: specific flag for "had access before, but it's no longer active"
  const hasExpiredAccess = (): boolean => {
    return !hasPurchasedCourse() && hasEverPurchasedCourse();
  };

  // Helper function to handle lesson click
  const handleLessonClick = (lesson: Lesson) => {
    if (hasPurchasedCourse()) {
      navigate(`/courses/${course?.hashed}/lessons/${lesson.hashed}`);
    } else {
      setPurchaseModalOpen(true);
    }
  };

  // Check if all lessons are completed
  const isAllLessonsCompleted = (): boolean => {
    if (!lessons || lessons.length === 0) return false;
    const completedCount = lessons.filter((lesson: Lesson) => 
      lessonProgress.some((p: LessonProgress) => p.lesson.id === lesson.id && p.is_completed)
    ).length;
    return completedCount === lessons.length;
  };

  // Check if user has passed a specific exam
  const hasPassedExam = (examId: number): boolean => {
    return examAttempts.some((attempt: any) =>
      attempt.exam_id === examId && attempt.is_passed
    );
  };

  // Check if user has completed course and passed at least one exam (for this course)
  const hasCompletedCourseAndPassedExam = (): boolean => {
    return hasPurchasedCourse() && isAllLessonsCompleted() && exams.some(exam => hasPassedExam(exam.id));
  };

  // Get a passed attempt for this course (for linking to exam result)
  const getPassedAttemptForCourse = (): { attempt: any; exam: Exam } | null => {
    if (!course || !examAttempts.length || !exams.length) return null;
    const passed = examAttempts.find(
      (a: any) => a.is_passed && a.submitted_at && exams.some(e => e.id === a.exam_id)
    );
    if (!passed) return null;
    const exam = exams.find(e => e.id === passed.exam_id);
    return exam ? { attempt: passed, exam } : null;
  };

  // Filter out passed exams from the display
  const availableExams = exams.filter(exam => !hasPassedExam(exam.id));

  if (loading) {
    return <LoadingSpinner message="Loading course details..." />;
  }

  if (!course) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          Course not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          height: { xs: '400px', md: '500px' },
          background: `url(${course.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.6) 100%)',
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(to bottom, transparent 0%, rgba(15, 23, 42, 1) 100%)',
            zIndex: 2,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, height: '100%' }}>
          {/* Breadcrumb */}
          <Box sx={{ pt: 6 }}>
            <Breadcrumb 
              items={[
                { label: 'Home', path: '/' },
                { label: 'Courses', path: '/courses' },
                { label: course.name }
              ]} 
            />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 4,
              color: 'white',
            }}
          >
            <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              {course.name}
            </Typography>
            
           
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
       
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 6, mt: 4 }}>
          <Box sx={{ flex: '2 1 0', minWidth: 0 }}>
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                About This Course
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: '1.1rem', 
                  lineHeight: 1.8, 
                  color: 'text.secondary',
                  maxWidth: '800px',
                }}
              >
                {course.description}
              </Typography>
            </Box>

            {lessons.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <VideoLibrary sx={{ mr: 2, color: '#10b981', fontSize: '2rem' }} />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Course Curriculum
                  </Typography>
                  <Badge 
                    badgeContent={lessons.length} 
                    color="primary" 
                    sx={{ ml: 2 }}
                  >
                    <Chip 
                      label={`${lessons.length} lessons`} 
                      color="primary" 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 600,
                        borderColor: '#10b981',
                        color: '#10b981'
                      }}
                    />
                  </Badge>
                </Box>

                <Card 
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.1)',
                    overflow: 'hidden'
                  }}
                >
                  <List sx={{ p: 0 }}>
                    {lessons.map((lesson, index) => {
                      const isPurchased = hasPurchasedCourse();
                      const isLastLesson = index === lessons.length - 1;
                      const progress = lessonProgress.find(p => p.lesson.id === lesson.id);
                      const isCompleted = progress?.is_completed || false;
                      
                      return (
                        <ListItem
                          key={lesson.id}
                          sx={{
                            borderBottom: isLastLesson ? 'none' : '1px solid rgba(0, 0, 0, 0.05)',
                            p: 0,
                            '&:hover': {
                              backgroundColor: isPurchased 
                                ? 'rgba(16, 185, 129, 0.05)' 
                                : 'rgba(0, 0, 0, 0.02)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleLessonClick(lesson)}
                            disabled={!isPurchased}
                            sx={{
                              p: 3,
                              '&:hover': {
                                backgroundColor: isPurchased 
                                  ? 'rgba(16, 185, 129, 0.1)' 
                                  : 'rgba(0, 0, 0, 0.05)',
                              },
                              '&.Mui-disabled': {
                                opacity: 0.6,
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 60 }}>
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: '50%',
                                  background: isPurchased 
                                    ? isCompleted
                                      ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                      : 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)'
                                    : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  fontSize: '1.1rem',
                                  boxShadow: isPurchased 
                                    ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                                    : '0 4px 12px rgba(107, 114, 128, 0.3)',
                                  position: 'relative',
                                }}
                              >
                                {isPurchased ? (
                                  isCompleted ? (
                                    <CheckCircle sx={{ fontSize: '1.5rem', color: '#fff' }} />
                                  ) : (
                                    <PlayArrow sx={{ fontSize: '1.5rem' }} />
                                  )
                                ) : (
                                  <Lock sx={{ fontSize: '1.2rem' }} />
                                )}
                                {isPurchased && !isCompleted && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: -2,
                                      right: -2,
                                      width: 16,
                                      height: 16,
                                      borderRadius: '50%',
                                      backgroundColor: '#10b981',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <CheckCircle sx={{ fontSize: '0.8rem', color: 'white' }} />
                                  </Box>
                                )}
                              </Box>
                            </ListItemIcon>
                            
                            <ListItemText
                              primaryTypographyProps={{ component: 'div' }}
                              secondaryTypographyProps={{ component: 'div' }}
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <Typography 
                                    variant="h6" 
                                    component="div"
                                    sx={{ 
                                      fontWeight: 600, 
                                      color: isPurchased ? 'text.primary' : 'text.secondary',
                                      mr: 2
                                    }}
                                  >
                                    {lesson.title}
                                  </Typography>
                                  {!isPurchased && (
                                    <Chip 
                                      label="Locked" 
                                      size="small" 
                                      color="default"
                                      variant="outlined"
                                      sx={{ 
                                        fontSize: '0.75rem',
                                        height: 20,
                                        '& .MuiChip-label': { px: 1 }
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography 
                                    variant="body2"
                                    component="div"
                                    sx={{ 
                                      color: 'text.secondary', 
                                      lineHeight: 1.6,
                                      mb: 1,
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {lesson.description}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                      <AccessTime sx={{ fontSize: '1rem', mr: 0.5 }} />
                                      <Typography variant="caption">
                                        Lesson {index + 1}
                                      </Typography>
                                    </Box>
                                    {isPurchased && (
                                      <Chip
                                        icon={isCompleted ? <CheckCircle /> : undefined}
                                        label={isCompleted ? 'Completed' : 'Not completed'}
                                        size="small"
                                        sx={{
                                          backgroundColor: isCompleted ? 'rgba(22, 163, 74, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                          color: isCompleted ? '#16a34a' : '#ef4444',
                                          fontWeight: 600,
                                          fontSize: '0.65rem',
                                          height: '20px',
                                          '& .MuiChip-icon': { 
                                            color: 'inherit',
                                            fontSize: '0.85rem'
                                          }
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              }
                            />
                            
                            <Box sx={{ ml: 2 }}>
                              {isPurchased ? (
                                <IconButton 
                                  sx={{ 
                                    color: isCompleted ? '#16a34a' : '#10b981',
                                    '&:hover': {
                                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                    }
                                  }}
                                >
                                  <ArrowForward />
                                </IconButton>
                              ) : (
                                <Tooltip title="Purchase course to unlock">
                                  <IconButton disabled>
                                    <Lock />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Card>

                {!hasPurchasedCourse() && (
                  <Box 
                    sx={{ 
                      mt: 3, 
                      p: 3, 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      {hasExpiredAccess() ? '🔒 Access Expired' : '🔒 Unlock All Lessons'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      {hasExpiredAccess()
                        ? `Your access to this course has expired. Renew to unlock all ${lessons.length} lessons again.`
                        : `Purchase this course to access all ${lessons.length} lessons and start your learning journey!`}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setPurchaseModalOpen(true)}
                      sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                        },
                      }}
                    >
                      {hasExpiredAccess() ? 'Renew Access' : 'Purchase Course'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {hasPurchasedCourse() && isAllLessonsCompleted() && availableExams.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <Assignment sx={{ mr: 2, color: '#f59e0b', fontSize: '2rem' }} />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Course Exams
                  </Typography>
                  <Tooltip title="Exams available after course completion">
                    <Chip 
                      label="Available" 
                      color="secondary" 
                      variant="outlined"
                      icon={<EmojiEvents />}
                      sx={{ 
                        ml: 2,
                        fontWeight: 600,
                        borderColor: '#f59e0b',
                        color: '#f59e0b',
                        '& .MuiChip-icon': { color: '#f59e0b' }
                      }}
                    />
                  </Tooltip>
                </Box>

                <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                  {availableExams.map((exam) => (
                    <Card 
                      key={exam.id}
                      sx={{ 
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(245, 158, 11, 0.1)',
                        border: '1px solid rgba(245, 158, 11, 0.2)',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 40px rgba(245, 158, 11, 0.2)',
                        }
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          {exam.title}
                        </Typography>
                        <Box 
                          sx={{ 
                            mb: 3, 
                            minHeight: '3em',
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                            '& p': {
                              margin: 0,
                              marginBottom: '0.5rem'
                            },
                            '& strong': {
                              fontWeight: 700
                            },
                            '& em': {
                              fontStyle: 'italic'
                            },
                            '& ul, & ol': {
                              paddingLeft: '1.5rem',
                              marginBottom: '0.5rem'
                            },
                            '& li': {
                              marginBottom: '0.25rem'
                            },
                            '& img': {
                              maxWidth: '100%',
                              height: 'auto',
                              borderRadius: '4px',
                              marginTop: '0.5rem',
                              marginBottom: '0.5rem'
                            }
                          }}
                          dangerouslySetInnerHTML={{ 
                            __html: exam.description || 'Test your knowledge of the course material.' 
                          }}
                        />
                        
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <AccessTime sx={{ fontSize: '1rem', mr: 0.5 }} />
                            <Typography variant="caption">
                              {exam.duration_minutes ? `${exam.duration_minutes} min` : 'No limit'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <Assignment sx={{ fontSize: '1rem', mr: 0.5 }} />
                            <Typography variant="caption">
                              Min. score: {exam.passing_score}%
                            </Typography>
                          </Box>
                        </Box>

                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => navigate(`/courses/${course?.hashed}/exams/${exam.hashed}`)}
                          sx={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                            },
                          }}
                        >
                          Take Exam
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {hasPurchasedCourse() && !isAllLessonsCompleted() && availableExams.length > 0 && (
              <Box 
                sx={{ 
                  mb: 6, 
                  p: 3, 
                  borderRadius: 2, 
                  backgroundColor: 'rgba(245, 158, 11, 0.05)',
                  border: '1px dashed #f59e0b',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Lock sx={{ color: '#f59e0b' }} />
                  <Typography variant="h6" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                    Exams Locked
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Complete all lessons to unlock the course exams and earn your certification.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Progress: {lessons.filter((lesson: Lesson) => 
                      lessonProgress.some((p: LessonProgress) => p.lesson.id === lesson.id && p.is_completed)
                    ).length} / {lessons.length} lessons completed
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
                  💡 Tip: Open each lesson and click the "Mark Complete" button (especially for text-only lessons without videos)
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: '1 1 0', minWidth: '300px' }}>
            {/* Instructor Section */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                  Your Mentor
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    src={course.mentor.image}
                    alt={course.mentor.name}
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      mr: 3,
                      border: '3px solid rgba(16, 185, 129, 0.3)',
                      boxShadow: '0 8px 25px rgba(16, 185, 129, 0.2)',
                    }}
                  />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {course.mentor.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {course.mentor.email}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  {course.mentor.description}
                </Typography>
                {course.course_owner && (
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(16, 185, 129, 0.2)' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 1.5 }}>
                      Provided by
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {course.course_owner.image && (
                        <Box
                          component="img"
                          src={course.course_owner.image}
                          alt={course.course_owner.naran}
                          sx={{
                            height: 40,
                            width: 'auto',
                            maxWidth: 120,
                            objectFit: 'contain',
                          }}
                        />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {course.course_owner.naran}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {!hasPurchasedCourse() && (
              <Box
                sx={{
                  p: 4,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.6) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  position: 'sticky',
                  top: 24,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  {hasExpiredAccess() ? 'Access Expired' : 'Ready to Start?'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#10b981' }}>
                  ${course.price}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                  {hasExpiredAccess()
                    ? 'Your previous access to this course has expired. Renew now to continue learning.'
                    : 'Enroll in this class to access the learning materials.'}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => setPurchaseModalOpen(true)}
                  sx={{ 
                    py: 2, 
                    mb: 3,
                    background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 35px rgba(16, 185, 129, 0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {hasExpiredAccess() ? 'Renew Access' : 'Enroll in Course'}
                </Button>
                
                {/* Payment Help Link */}
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setPaymentHelpModalOpen(true)}
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      textTransform: 'none',
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                      '&:hover': {
                        color: '#10b981',
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Info sx={{ fontSize: '1rem', mr: 0.5 }} />
                    Oinsá atu selu?
                  </Button>
                </Box>
              </Box>
            )}

            {hasCompletedCourseAndPassedExam() && getPassedAttemptForCourse() && (
              <Box
                sx={{
                  p: 4,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(34, 197, 94, 0.08) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  position: 'sticky',
                  top: 24,
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <EmojiEvents sx={{ fontSize: '3rem', color: '#10b981', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    Course completed
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    You&apos;ve completed this course and passed the exam.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => {
                    const { attempt, exam } = getPassedAttemptForCourse()!;
                    navigate(`/courses/${course?.hashed}/exams/${exam.hashed}/result/${attempt.id}`);
                  }}
                  sx={{
                    py: 2,
                    background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 35px rgba(16, 185, 129, 0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  View Exam Result
                </Button>
              </Box>
            )}

            {hasPurchasedCourse() && !hasCompletedCourseAndPassedExam() && (
              <Box
                sx={{
                  p: 4,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  position: 'sticky',
                  top: 24,
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <CheckCircle sx={{ fontSize: '3rem', color: '#10b981', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    Course Access Granted!
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    Your access to this course is unlocked. Start learning and build new skills today.
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => {
                    if (lessons.length > 0) {
                      navigate(`/courses/${course?.hashed}/lessons/${lessons[0].hashed}`);
                    }
                  }}
                  sx={{ 
                    py: 2,
                    background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 35px rgba(16, 185, 129, 0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {lessons.length > 0 ? 'Start Learning' : 'View Course'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      <PurchaseModal
        open={purchaseModalOpen}
        onClose={() => setPurchaseModalOpen(false)}
        course={course}
        onPurchaseSubmitted={() => {
          // Keep modal mounted so success state can render, then refresh data in background.
          void fetchCourseData(false);
        }}
      />

      <PaymentHelpModal
        open={paymentHelpModalOpen}
        onClose={() => setPaymentHelpModalOpen(false)}
        coursePrice={course?.price || 0}
        onEnroll={() => setPurchaseModalOpen(true)}
      />
    </Box>
  );
};

export default CourseDetail;